import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LucideDynamicIcon } from "@lucide/angular";
import { backend } from "@/integrations/backend/client";
import {
  resolveLocalizedText,
  type LocalizedTextMap,
} from "@/lib/localizedContent";
import { cn } from "@/lib/utils";
import { AuthService, User } from "../core/auth.service";
import { LanguageService } from "../core/language.service";
import { ToastService } from "../core/toast.service";
import { LanguageSelectorComponent } from "../shared/language-selector.component";
import { buttonClasses, cardClasses, progressBarStyle } from "../shared/ui";
import { localQuestionBank } from "../game/question-bank.fixture";
import {
  getQuestionBankStationIdsForIsland,
  isQuestionBankStationForIsland,
  questionBankStationOrderIndex,
} from "../game/question-bank-route";
import {
  advancePlaytestQuestion,
  advancePlaytestStage,
  clampStage,
  applyPlaytestAnswer,
  clampLevelForStage,
  createPlaytestSession,
  getPlayableQuestions,
  getPlaytestQuestion,
  getQuestionSourceKey,
  getStartingLevelFromAssessment,
  type PlaytestSession,
} from "../game/playtest-journey";
import {
  clearStoredPlaytestProgress,
  getPlaytestProgressKey,
  loadStoredPlaytestProgress,
  saveStoredPlaytestProgress,
  type PlaytestProgressSnapshot,
} from "../game/playtest-progress";
import { syncCurrentUserGameProgress } from "../game/game-progress-sync";

interface Island {
  id: string;
  name: string;
  name_translations?: LocalizedTextMap;
  slug: string;
  description: string;
  description_translations?: LocalizedTextMap;
  order_index?: number;
  crew_id?: string | null;
}

interface Question {
  id: string;
  source_question_id?: string | null;
  station_id?: string | null;
  question_text: string;
  question_text_translations?: LocalizedTextMap;
  explanation?: string | null;
  order_index: number;
  stage: number;
  point_value?: number | null;
  complexity?: number | null;
  status?: string | null;
  human_review?: { decision?: string | null } | null;
}

interface Answer {
  id: string;
  question_id: string;
  answer_text: string;
  answer_text_translations?: LocalizedTextMap;
  order_index: number;
  is_correct?: boolean;
}

interface Station {
  id: string;
  title: string;
  slug: string;
  order_index: number;
  source_station_id?: string | null;
}

interface StationProgress {
  id?: string;
  user_id: string;
  station_id: string;
  current_stage: number;
  stage_points: number;
  station_points: number;
  completed_at?: string | null;
  updated_at?: string;
  state?: PlaytestProgressSnapshot | null;
}

interface CompleteIslandResult {
  completed?: boolean;
  score?: number;
  completed_station_count?: number;
  station_count?: number;
}

interface IslandAccessResult {
  can_access?: boolean;
  previous_island_id?: string | null;
  previous_island_name?: string | null;
  waiting_on_count?: number | null;
}

type QuizState =
  | "intro"
  | "stage-intro"
  | "quiz"
  | "feedback"
  | "stage-results"
  | "results";

interface CheckAnswerResult {
  is_correct?: boolean;
  explanation?: string | null;
  selected_answer_text?: string | null;
  correct_answer_text?: string | null;
  point_value?: number | null;
  earned_points?: number | null;
}

const STAGE_ICONS = ["🌱", "⚡", "🏆"];
const STAGE_COLORS = [
  "from-emerald-400 to-emerald-600",
  "from-amber-400 to-orange-500",
  "from-violet-500 to-purple-600",
];
const SPEED_BONUS_WINDOWS = [
  { seconds: 5, bonus: 3 },
  { seconds: 10, bonus: 2 },
  { seconds: 15, bonus: 1 },
] as const;
function getQuestionPointValue(question?: Question | null) {
  const pointValue = Number(
    question?.complexity ?? question?.point_value ?? 10,
  );
  return Number.isFinite(pointValue) ? Math.max(1, pointValue) : 10;
}

function getQuestionSetPoints(questions: Question[]) {
  return questions.reduce(
    (sum, question) => sum + getQuestionPointValue(question),
    0,
  );
}

function getSpeedBonusPoints(answerSeconds: number) {
  const bonusWindow = SPEED_BONUS_WINDOWS.find(
    (item) => answerSeconds <= item.seconds,
  );
  return bonusWindow?.bonus ?? 0;
}

function getIslandLevelBoost(orderIndex?: number) {
  const normalizedOrder = Math.max(1, Number(orderIndex ?? 1));
  return Math.min(3, Math.floor((normalizedOrder - 1) / 2));
}

function normalizedStationRouteKey(value?: string | null) {
  return value?.trim().toLowerCase().replace(/-/g, "_") ?? "";
}

function chunkArray<T>(items: T[], size: number) {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

function createQuizSoundPlayer() {
  try {
    const audioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;

    if (!audioContextClass) return () => undefined;

    const audioContext = new audioContextClass();
    void audioContext.resume();

    return (correct: boolean) => {
      const gain = audioContext.createGain();
      const now = audioContext.currentTime;
      const notes = correct ? [523.25, 659.25, 783.99] : [246.94, 196.0];

      gain.connect(audioContext.destination);
      gain.gain.setValueAtTime(0.0001, now);

      notes.forEach((frequency, index) => {
        const start = now + index * 0.09;
        const oscillator = audioContext.createOscillator();

        oscillator.type = correct ? "triangle" : "sawtooth";
        oscillator.frequency.setValueAtTime(frequency, start);
        oscillator.connect(gain);
        oscillator.start(start);
        oscillator.stop(start + 0.11);
      });

      gain.gain.exponentialRampToValueAtTime(0.16, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);

      window.setTimeout(() => void audioContext.close(), 520);
    };
  } catch {
    return () => undefined;
  }
}

@Component({
  selector: "app-island-page",
  standalone: true,
  imports: [CommonModule, LanguageSelectorComponent, LucideDynamicIcon],
  template: `
    <div
      *ngIf="loading"
      class="min-h-screen ocean-gradient flex items-center justify-center"
    >
      <svg
        lucideIcon="compass"
        class="w-16 h-16 animate-spin text-primary-foreground"
      ></svg>
    </div>

    <div
      *ngIf="!loading && !island"
      class="min-h-screen ocean-gradient flex items-center justify-center"
    >
      <div [class]="cardClasses('p-8 text-center')">
        <h2 class="text-xl font-bold mb-4">
          {{ language.t("island.notFound") }}
        </h2>
        <button [class]="buttonClasses()" (click)="router.navigateByUrl('/')">
          {{ language.t("common.returnToMap") }}
        </button>
      </div>
    </div>

    <div
      *ngIf="!loading && island"
      class="min-h-screen ocean-gradient relative overflow-hidden"
    >
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-primary/20 to-transparent animate-wave"
        ></div>
      </div>

      <div
        class="relative z-10 flex flex-wrap items-center justify-between gap-3 p-3 sm:p-4"
      >
        <button
          [class]="
            buttonClasses(
              'ghost',
              'default',
              'text-primary-foreground hover:bg-primary-foreground/20'
            )
          "
          (click)="router.navigateByUrl('/')"
        >
          <svg lucideIcon="arrow-left" class="w-5 h-5 mr-2"></svg>
          {{ language.t("common.backToMap") }}
        </button>
        <div class="flex items-center gap-3">
          <div
            *ngIf="
              quizState === 'quiz' ||
              quizState === 'feedback' ||
              quizState === 'stage-results'
            "
            class="flex items-center gap-2 rounded-full bg-amber-100/95 px-4 py-2 text-sm font-bold text-amber-900 shadow-lg"
          >
            <svg
              lucideIcon="star"
              class="w-4 h-4 text-amber-500 fill-amber-500"
            ></svg>
            {{ scoreboardPoints }} pts
          </div>
          <app-language-selector
            [compact]="true"
            className="border-primary-foreground/20 bg-background/20 text-primary-foreground"
          />
        </div>
      </div>

      <main
        class="relative z-10 container mx-auto px-3 py-4 flex items-center justify-center min-h-[calc(100vh-100px)] sm:px-4 sm:py-8"
      >
        <section
          *ngIf="quizState === 'intro'"
          class="max-w-5xl w-full animate-in slide-in-from-bottom-4 fade-in"
        >
          <div
            [class]="
              cardClasses(
                'p-5 sm:p-8 bg-card/95 backdrop-blur-sm shadow-2xl border-0'
              )
            "
          >
            <div class="text-center">
              <div
                class="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center shadow-lg"
              >
                <svg
                  lucideIcon="anchor"
                  class="w-10 h-10 text-primary-foreground"
                ></svg>
              </div>

              <h1 class="text-3xl font-bold text-foreground mb-2">
                {{ island.name }}
              </h1>
              <p class="text-muted-foreground mb-6">{{ island.description }}</p>

              <div
                *ngIf="stations.length > 0; else noQuestions"
                class="grid gap-4 sm:grid-cols-2"
              >
                <button
                  *ngFor="let station of stations"
                  type="button"
                  [class]="stationCardClass(station.id)"
                  [disabled]="isStationComplete(station.id)"
                  (click)="startStation(station.id)"
                >
                  <div class="flex items-start justify-between gap-4">
                    <div
                      class="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-md"
                    >
                      <svg
                        lucideIcon="compass"
                        class="w-6 h-6 text-primary-foreground"
                      ></svg>
                    </div>
                    <div class="text-right">
                      <span class="block text-sm font-bold text-amber-700"
                        >{{ stationPoints(station.id) }} pts</span
                      >
                      <span
                        class="mt-1 block text-xs font-semibold text-muted-foreground"
                        >{{ stationStatusText(station.id) }}</span
                      >
                    </div>
                  </div>
                  <h2 class="mt-5 text-lg font-bold text-foreground">
                    {{ station.title }}
                  </h2>
                  <p class="mt-3 text-sm text-muted-foreground">
                    3 stages - 10 base pts each + speed bonus
                  </p>
                </button>
              </div>
              <ng-template #noQuestions>
                <p class="text-amber-600 font-medium">
                  {{ language.t("island.noQuestions") }}
                </p>
              </ng-template>
            </div>
          </div>
        </section>

        <section
          *ngIf="quizState === 'stage-intro'"
          class="max-w-xl w-full animate-in zoom-in-95 fade-in"
        >
          <div
            [class]="
              cardClasses(
                'p-5 sm:p-8 bg-card/95 backdrop-blur-sm shadow-2xl border-0'
              )
            "
          >
            <div class="text-center">
              <div class="flex justify-center gap-2 mb-6">
                <div
                  *ngFor="let stage of [1, 2, 3]"
                  class="w-3 h-3 rounded-full transition-all"
                  [ngClass]="
                    completedStages.includes(stage)
                      ? 'bg-accent'
                      : stage === currentStage
                        ? 'bg-primary animate-pulse'
                        : 'bg-muted'
                  "
                ></div>
              </div>

              <div [class]="currentStageIconClass()">
                {{ stageIcons[currentStage - 1] }}
              </div>

              <h2 class="text-2xl font-bold text-foreground mb-1">
                {{ language.t("island.stage") }} {{ currentStage }}:
                {{ language.t("stage." + currentStage) }}
              </h2>
              <p class="text-muted-foreground mb-6">
                {{
                  language.t("island.questionsToAdvance", {
                    count: stageQuestions.length,
                    points: stagePossibleScore,
                  })
                }}
              </p>
              <p
                class="mb-6 rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-900"
              >
                {{ language.t("island.speedBonusHint") }}
              </p>

              <button
                [class]="stageButtonClass(currentStage, 'w-full')"
                (click)="startStage()"
              >
                <svg lucideIcon="star" class="w-5 h-5 mr-2"></svg>
                {{ language.t("island.startStage", { stage: currentStage }) }}
              </button>
            </div>
          </div>
        </section>

        <section
          *ngIf="
            (quizState === 'quiz' || quizState === 'feedback') &&
            currentQuestion
          "
          class="max-w-2xl w-full animate-in slide-in-from-right-8 fade-in"
        >
          <div
            [class]="
              cardClasses(
                'p-5 sm:p-8 bg-card/95 backdrop-blur-sm shadow-2xl border-0'
              )
            "
          >
            <div class="flex items-center gap-2 mb-4">
              <div [class]="stagePillClass(currentStage)">
                {{ stageIcons[currentStage - 1] }}
                {{ language.t("island.stage") }} {{ currentStage }}
              </div>
              <div
                *ngIf="currentStation"
                class="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary"
              >
                {{ currentStation.title }}
              </div>
            </div>

            <div
              class="relative mb-6 h-2 w-full overflow-hidden rounded-full bg-secondary"
            >
              <div
                class="h-full w-full flex-1 bg-primary transition-all"
                [ngStyle]="progressBarStyle(stageProgressPercent)"
              ></div>
            </div>

            <div
              *ngIf="quizState === 'quiz'"
              class="mb-4 flex items-center gap-2 rounded-full bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-900"
            >
              <svg lucideIcon="timer" class="h-4 w-4"></svg>
              {{ language.t("island.speedBonusHint") }}
            </div>

            <h2 class="text-xl font-semibold text-foreground mb-6">
              {{ currentQuestion.question_text }}
            </h2>

            <div class="space-y-3 mb-6">
              <button
                *ngFor="let answer of currentAnswers; let index = index"
                type="button"
                (click)="selectAnswer(answer.id)"
                [disabled]="quizState === 'feedback' || submittingAnswer"
                [class]="answerChoiceClass(answer.id)"
              >
                <span class="font-medium">{{ answerLetter(index) }}.</span>
                {{ answer.answer_text }}
              </button>
            </div>

            <div
              *ngIf="quizState === 'feedback'"
              class="mb-6 rounded-2xl border p-5"
              [ngClass]="
                isCorrect
                  ? 'border-accent/50 bg-accent/10'
                  : 'border-destructive/40 bg-destructive/10'
              "
            >
              <div class="flex items-start gap-4">
                <svg
                  [lucideIcon]="isCorrect ? 'circle-check-big' : 'circle-x'"
                  class="w-8 h-8 mt-1"
                  [ngClass]="isCorrect ? 'text-accent' : 'text-destructive'"
                ></svg>
                <div class="space-y-2 text-left">
                  <h3 class="text-lg font-bold text-foreground">
                    {{
                      isCorrect
                        ? language.t("island.correct")
                        : language.t("island.notQuite")
                    }}
                  </h3>
                  <p class="font-semibold text-primary">
                    +{{ lastEarnedPoints }}
                    {{ language.t("common.pointsShort") }}
                  </p>
                  <p
                    *ngIf="lastSpeedBonusPoints > 0"
                    class="text-sm font-semibold text-amber-700"
                  >
                    {{
                      language.t("island.speedBonusEarned", {
                        points: lastSpeedBonusPoints,
                        seconds: lastAnswerSeconds,
                      })
                    }}
                  </p>
                  <p
                    *ngIf="selectedAnswerText"
                    class="text-sm text-muted-foreground"
                  >
                    {{ language.t("island.yourAnswer") }}:
                    {{ selectedAnswerText }}
                  </p>
                  <p
                    *ngIf="!isCorrect && correctAnswerText"
                    class="text-sm text-accent"
                  >
                    {{ language.t("island.correctAnswer") }}:
                    {{ correctAnswerText }}
                  </p>
                  <p class="text-sm text-muted-foreground">
                    {{
                      currentExplanation ||
                        (isCorrect
                          ? language.t("island.defaultRight")
                          : language.t("island.defaultWrong"))
                    }}
                  </p>
                </div>
              </div>
            </div>

            <button
              *ngIf="quizState === 'quiz'"
              [class]="stageButtonClass(currentStage, 'w-full')"
              [disabled]="!selectedAnswerId || submittingAnswer"
              (click)="submitAnswer()"
            >
              <svg
                *ngIf="submittingAnswer"
                lucideIcon="loader-circle"
                class="h-4 w-4 animate-spin"
              ></svg>
              {{
                submittingAnswer
                  ? language.t("island.checking")
                  : language.t("island.submitAnswer")
              }}
            </button>

            <button
              *ngIf="quizState === 'feedback'"
              [class]="stageButtonClass(currentStage, 'w-full')"
              (click)="nextQuestion()"
            >
              {{
                isCurrentStageFinished()
                  ? language.t("island.completeStage")
                  : language.t("island.nextQuestion")
              }}
            </button>
          </div>
        </section>

        <section
          *ngIf="false"
          class="max-w-2xl w-full animate-in zoom-in-95 fade-in"
        >
          <div
            [class]="
              cardClasses(
                'p-5 sm:p-8 bg-card/95 backdrop-blur-sm shadow-2xl border-0'
              )
            "
          >
            <div
              class="text-center mb-6 p-6 rounded-lg"
              [ngClass]="isCorrect ? 'bg-accent/20' : 'bg-destructive/20'"
            >
              <svg
                [lucideIcon]="isCorrect ? 'circle-check-big' : 'circle-x'"
                class="w-16 h-16 mx-auto mb-3"
                [ngClass]="isCorrect ? 'text-accent' : 'text-destructive'"
              ></svg>
              <h3
                class="text-2xl font-bold"
                [ngClass]="isCorrect ? 'text-accent' : 'text-destructive'"
              >
                {{
                  isCorrect
                    ? language.t("island.correct")
                    : language.t("island.notQuite")
                }}
              </h3>
              <p class="mt-2 text-sm font-semibold text-foreground">
                {{
                  isCorrect
                    ? "+" +
                      lastEarnedPoints +
                      " " +
                      language.t("common.pointsShort")
                    : "0/" +
                      questionPointValue(currentQuestion) +
                      " " +
                      language.t("common.pointsShort")
                }}
              </p>
            </div>

            <div class="bg-primary/10 rounded-lg p-4 mb-6 space-y-3">
              <h4 class="font-semibold text-foreground">
                {{
                  isCorrect
                    ? language.t("island.whyRight")
                    : language.t("island.whyWrong")
                }}
              </h4>

              <div
                *ngIf="selectedAnswerText"
                class="rounded-md bg-background/70 p-3"
              >
                <p
                  class="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {{ language.t("island.yourAnswer") }}
                </p>
                <p class="text-sm text-foreground">{{ selectedAnswerText }}</p>
              </div>

              <div
                *ngIf="!isCorrect && correctAnswerText"
                class="rounded-md border border-accent/30 bg-accent/10 p-3"
              >
                <p
                  class="text-xs font-semibold uppercase tracking-wide text-accent"
                >
                  {{ language.t("island.correctAnswer") }}
                </p>
                <p class="text-sm text-foreground">{{ correctAnswerText }}</p>
              </div>

              <p class="text-muted-foreground">
                {{
                  currentExplanation ||
                    (isCorrect
                      ? language.t("island.defaultRight")
                      : language.t("island.defaultWrong"))
                }}
              </p>
            </div>

            <button
              [class]="stageButtonClass(currentStage, 'w-full')"
              (click)="nextQuestion()"
            >
              {{
                currentQuestionIndex < stageQuestions.length - 1
                  ? language.t("island.nextQuestion")
                  : language.t("island.completeStage")
              }}
            </button>
          </div>
        </section>

        <section
          *ngIf="quizState === 'stage-results'"
          class="max-w-xl w-full animate-in slide-in-from-bottom-4 fade-in"
        >
          <div
            [class]="
              cardClasses(
                'p-5 sm:p-8 bg-card/95 backdrop-blur-sm shadow-2xl border-0'
              )
            "
          >
            <div class="text-center">
              <div [class]="stageResultIconClass()">
                <ng-container *ngIf="stagePercent >= 70; else lockedStage">{{
                  stageIcons[currentStage - 1]
                }}</ng-container>
                <ng-template #lockedStage
                  ><svg lucideIcon="lock" class="w-10 h-10 text-white"></svg
                ></ng-template>
              </div>

              <h2 class="text-2xl font-bold text-foreground mb-2">
                {{
                  stagePercent >= 70
                    ? language.t("island.stageComplete", {
                        stage: currentStage,
                      })
                    : language.t("island.stageNotPassed", {
                        stage: currentStage,
                      })
                }}
              </h2>
              <p class="text-muted-foreground mb-6">
                {{
                  stagePercent >= 70
                    ? currentStage < 3
                      ? language.t("island.nextChallenge")
                      : language.t("island.islandConqueredStage")
                    : language.t("island.needToAdvance")
                }}
              </p>

              <div class="bg-muted/50 rounded-lg p-6 mb-6">
                <div class="text-5xl font-bold text-primary mb-2">
                  {{ stagePercent }}%
                </div>
                <div class="text-muted-foreground">
                  {{
                    language.t("island.stageScoreLine", {
                      score: stageScore,
                      possible: stagePossibleScore,
                      correct: stageCorrectCount,
                      total: stageQuestions.length,
                    })
                  }}
                </div>
              </div>

              <div class="mb-6">
                <div class="text-sm text-muted-foreground mb-2">
                  {{ language.t("island.progressTitle") }}
                </div>
                <div class="flex justify-center gap-3">
                  <div
                    *ngFor="let stage of [1, 2, 3]"
                    class="h-14 w-14 rounded-lg flex flex-col items-center justify-center transition-all sm:h-16 sm:w-16"
                    [ngClass]="stageProgressClass(stage)"
                  >
                    <span class="text-xl">{{ stageIcons[stage - 1] }}</span>
                    <span class="text-xs font-bold">S{{ stage }}</span>
                  </div>
                </div>
              </div>

              <div class="flex flex-col gap-3 sm:flex-row">
                <button
                  [class]="buttonClasses('outline', 'default', 'flex-1')"
                  (click)="router.navigateByUrl('/')"
                >
                  {{ language.t("common.backToMap") }}
                </button>
                <button
                  [class]="stageResultButtonClass()"
                  (click)="proceedAfterStage()"
                >
                  {{ stageResultActionLabel }}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section
          *ngIf="quizState === 'results'"
          class="max-w-xl w-full animate-in slide-in-from-bottom-4 fade-in"
        >
          <div
            [class]="
              cardClasses(
                'p-5 sm:p-8 bg-card/95 backdrop-blur-sm shadow-2xl border-0'
              )
            "
          >
            <div class="text-center">
              <div
                class="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg"
              >
                <svg lucideIcon="trophy" class="w-12 h-12 text-white"></svg>
              </div>

              <h2 class="text-3xl font-bold text-foreground mb-2">
                {{ language.t("island.conquered") }}
              </h2>
              <p class="text-muted-foreground mb-6">
                {{
                  language.t("island.conqueredSubtitle", { name: island.name })
                }}
              </p>

              <div class="bg-accent/20 rounded-lg p-6 mb-6">
                <div class="text-5xl font-bold text-accent mb-2">
                  {{ totalEarnedPoints }} {{ language.t("common.pointsShort") }}
                </div>
                <div class="text-muted-foreground">
                  {{
                    language.t("island.correctOverall", {
                      percent: finalPercent,
                      correct: finalCorrectCount,
                      total: totalQuestions,
                    })
                  }}
                </div>
              </div>

              <div class="flex justify-center gap-4 mb-6">
                <div
                  *ngFor="let stage of [1, 2, 3]"
                  [class]="
                    'w-16 h-16 rounded-full flex flex-col items-center justify-center text-white shadow-lg bg-gradient-to-br ' +
                    stageColors[stage - 1]
                  "
                >
                  <span class="text-2xl">{{ stageIcons[stage - 1] }}</span>
                </div>
              </div>

              <div class="flex flex-col gap-3 justify-center sm:flex-row">
                <button
                  [class]="
                    buttonClasses(
                      'default',
                      'default',
                      'flex-1 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground'
                    )
                  "
                  (click)="router.navigateByUrl('/')"
                >
                  {{ language.t("common.backToMap") }}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  `,
})
export class IslandPageComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  readonly language = inject(LanguageService);
  private readonly toast = inject(ToastService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  slug = "";
  user: User | null = null;
  island: Island | null = null;
  stations: Station[] = [];
  stationProgressRecords: StationProgress[] = [];
  allQuestions: Question[] = [];
  answers: Answer[] = [];
  previousQuestionSourceKeys = new Set<string>();
  usingLocalQuestionBank = false;
  loading = true;
  quizState: QuizState = "intro";
  currentStationId: string | null = null;
  playtestSession: PlaytestSession | null = null;
  currentStage = 1;
  currentQuestionIndex = 0;
  selectedAnswerId: string | null = null;
  submittingAnswer = false;
  stageScore = 0;
  totalScore = 0;
  stageCorrectCount = 0;
  totalCorrectCount = 0;
  attemptId: string | null = null;
  isCorrect = false;
  currentExplanation: string | null = null;
  selectedAnswerText: string | null = null;
  correctAnswerText: string | null = null;
  lastEarnedPoints = 0;
  lastSpeedBonusPoints = 0;
  lastAnswerSeconds = 0;
  completedStages: number[] = [];
  stageIcons = STAGE_ICONS;
  stageColors = STAGE_COLORS;
  private questionStartedAtMs: number | null = null;
  private subscriptions: Subscription[] = [];
  private loadIslandRequestId = 0;
  private remoteStationIdsByLocalStationId = new Map<string, string>();

  ngOnInit() {
    this.subscriptions.push(
      this.auth.user$.subscribe((user) => {
        const previousUserId = this.user?.id ?? null;
        const nextUserId = user?.id ?? null;
        this.user = user;
        if (previousUserId !== nextUserId) {
          this.resetRuntimeProgressState();
          if (this.slug) void this.loadIslandData(false);
        }
        this.changeDetector.detectChanges();
      }),
      this.route.paramMap.subscribe((params) => {
        const nextSlug = params.get("slug") || "";
        if (this.slug !== nextSlug) this.resetRuntimeProgressState();
        this.slug = nextSlug;
        this.changeDetector.detectChanges();
        if (this.slug) void this.loadIslandData();
      }),
      this.language.language$.subscribe(() => {
        this.changeDetector.detectChanges();
        if (this.slug) void this.loadIslandData(false);
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  get stageQuestions() {
    return getPlayableQuestions(
      this.allQuestions,
      this.currentStationId,
      this.currentStage,
    ) as Question[];
  }

  get totalQuestions() {
    return this.currentStationQuestions.length;
  }

  get stagePossibleScore() {
    return 10;
  }

  get totalPossibleScore() {
    return 30;
  }

  get currentQuestion() {
    if (!this.playtestSession) return null;
    return getPlaytestQuestion(
      this.allQuestions,
      this.playtestSession,
      this.currentStationId,
      Math.random,
      {
        avoidSourceKeys: this.previousQuestionSourceKeys,
      },
    ) as Question | undefined;
  }

  get currentStageHistory() {
    return this.playtestSession
      ? this.stageHistory(this.playtestSession, this.currentStage)
      : [];
  }

  get currentAnswers() {
    return this.answers.filter(
      (answer) => answer.question_id === this.currentQuestion?.id,
    );
  }

  get stageProgressPercent() {
    return this.stageQuestions.length > 0
      ? Math.min(
          100,
          (this.stageAnsweredCount / this.stageQuestions.length) * 100,
        )
      : 0;
  }

  get stagePercent() {
    return this.stageQuestions.length > 0
      ? Math.min(
          100,
          Math.round(
            (this.stageCorrectCount / this.stageQuestions.length) * 100,
          ),
        )
      : 0;
  }

  get stageAnsweredCount() {
    return this.currentStageHistory.length;
  }

  get totalEarnedPoints() {
    return this.completedStages.includes(this.currentStage)
      ? this.totalScore
      : this.totalScore + this.stageScore;
  }

  get finalPercent() {
    return this.totalPossibleScore > 0
      ? Math.min(
          100,
          Math.round((this.totalEarnedPoints / this.totalPossibleScore) * 100),
        )
      : 0;
  }

  get finalCorrectCount() {
    return this.completedStages.includes(this.currentStage)
      ? this.totalCorrectCount
      : this.totalCorrectCount + this.stageCorrectCount;
  }

  get currentStation() {
    return (
      this.stations.find((station) => station.id === this.currentStationId) ??
      null
    );
  }

  get currentStationQuestions() {
    return this.allQuestions.filter(
      (question) => question.station_id === this.currentStationId,
    );
  }

  get scoreboardPoints() {
    return this.playtestSession?.stationPoints ?? this.totalEarnedPoints;
  }

  get completedStationCount() {
    return this.stations.filter((station) => this.isStationComplete(station.id))
      .length;
  }

  get stageResultActionLabel() {
    if (this.stagePercent < 70) return this.language.t("island.retryStage");
    if (this.currentStage < 3)
      return this.language.t("island.startNextStage", {
        stage: this.currentStage + 1,
      });
    if (this.areAllStationsComplete())
      return this.language.t("island.completeIsland");
    return this.language.t("island.chooseAnotherStation");
  }

  private isIslandLoadCurrent(
    requestId: number,
    slug: string,
    userId: string | null,
    language: string,
  ) {
    return (
      requestId === this.loadIslandRequestId &&
      this.slug === slug &&
      (this.user?.id ?? null) === userId &&
      this.language.language === language
    );
  }

  async loadIslandData(showSpinner = true) {
    const requestId = ++this.loadIslandRequestId;
    const requestedSlug = this.slug;
    const requestedUserId = this.user?.id ?? null;
    const requestedLanguage = this.language.language;

    if (showSpinner) {
      this.loading = true;
      this.changeDetector.detectChanges();
    }
    try {
      this.usingLocalQuestionBank = false;
      this.stationProgressRecords = [];
      this.previousQuestionSourceKeys = new Set<string>();
      this.remoteStationIdsByLocalStationId = new Map<string, string>();

      const { data: islandData, error: islandError } = await backend
        .from("islands")
        .select("*")
        .eq("slug", requestedSlug)
        .maybeSingle();

      if (islandError) throw islandError;
      if (
        !this.isIslandLoadCurrent(
          requestId,
          requestedSlug,
          requestedUserId,
          requestedLanguage,
        )
      )
        return;
      if (!islandData) {
        this.toast.error(this.language.t("island.notFound"));
        await this.router.navigateByUrl("/");
        return;
      }

      this.island = {
        ...(islandData as Island),
        name: resolveLocalizedText(
          islandData.name,
          islandData.name_translations,
          requestedLanguage,
        ),
        description: resolveLocalizedText(
          islandData.description,
          islandData.description_translations,
          requestedLanguage,
        ),
      };

      if (requestedUserId) {
        const canOpenSequence = await this.ensureSequentialIslandAccess(
          this.island,
        );
        if (
          !this.isIslandLoadCurrent(
            requestId,
            requestedSlug,
            requestedUserId,
            requestedLanguage,
          )
        )
          return;
        if (!canOpenSequence) return;

        const canOpen = await this.ensureCrewIslandAccess(islandData.id);
        if (
          !this.isIslandLoadCurrent(
            requestId,
            requestedSlug,
            requestedUserId,
            requestedLanguage,
          )
        )
          return;
        if (!canOpen) return;
      }

      const { data: stationsData, error: stationsError } = await backend
        .from("stations")
        .select("*")
        .eq("island_id", islandData.id)
        .order("order_index");

      if (stationsError) throw stationsError;
      if (
        !this.isIslandLoadCurrent(
          requestId,
          requestedSlug,
          requestedUserId,
          requestedLanguage,
        )
      )
        return;
      this.stations = ((stationsData as unknown as Station[]) || [])
        .filter((station) =>
          isQuestionBankStationForIsland(requestedSlug, station),
        )
        .sort((a, b) => {
          const routeIndexA = questionBankStationOrderIndex(requestedSlug, a);
          const routeIndexB = questionBankStationOrderIndex(requestedSlug, b);
          if (routeIndexA >= 0 && routeIndexB >= 0) {
            return routeIndexA - routeIndexB;
          }
          return a.order_index - b.order_index;
        });
      this.remoteStationIdsByLocalStationId =
        this.createRemoteStationIdMap(this.stations);
      const remoteStationIdSet = new Set(
        this.stations.map((station) => station.id),
      );

      const { data: questionsData, error: questionsError } = await backend
        .from("questions_public")
        .select("*")
        .eq("island_id", islandData.id)
        .order("stage")
        .order("order_index");

      if (questionsError) throw questionsError;
      if (
        !this.isIslandLoadCurrent(
          requestId,
          requestedSlug,
          requestedUserId,
          requestedLanguage,
        )
      )
        return;
      this.allQuestions = ((questionsData as unknown as Question[]) || [])
        .filter(
          (question) =>
            question.station_id && remoteStationIdSet.has(question.station_id),
        )
        .map((question) => ({
          ...question,
          question_text: resolveLocalizedText(
            question.question_text,
            question.question_text_translations,
            requestedLanguage,
          ),
        }));

      if (this.allQuestions.length > 0) {
        const questionIds = this.allQuestions.map((question) => question.id);
        const answerRows: Answer[] = [];

        for (const questionIdChunk of chunkArray(questionIds, 120)) {
          const { data: answersData, error: answersError } = await backend
            .from("answers_public")
            .select("*")
            .in("question_id", questionIdChunk)
            .order("order_index");

          if (answersError) throw answersError;
          if (
            !this.isIslandLoadCurrent(
              requestId,
              requestedSlug,
              requestedUserId,
              requestedLanguage,
            )
          )
            return;
          answerRows.push(...((answersData as unknown as Answer[]) || []));
        }

        this.answers = answerRows.map((answer) => ({
          ...answer,
          answer_text: resolveLocalizedText(
            answer.answer_text,
            answer.answer_text_translations,
            requestedLanguage,
          ),
        }));
      } else {
        this.answers = [];
      }

      const playableRemoteQuestions = this.allQuestions.filter(
        (question) =>
          question.station_id &&
          question.complexity &&
          question.status === "approved" &&
          question.human_review?.decision === "approved",
      );
      const hasCompleteRemoteStages =
        this.stations.length > 0 &&
        this.stations.every((station) =>
          [1, 2, 3].every(
            (stage) =>
              getPlayableQuestions(this.allQuestions, station.id, stage)
                .length > 0,
          ),
        );

      if (this.stations.length === 0 || !hasCompleteRemoteStages) {
        console.warn(
          "Remote stages are incomplete; using local question bank.",
          {
            stationCount: this.stations.length,
            playableQuestionCount: playableRemoteQuestions.length,
          },
        );
        this.applyLocalQuestionBank();
        if (requestedUserId) {
          await this.repairCompletedIslandProgress(requestedUserId);
        }
        return;
      }

      this.previousQuestionSourceKeys =
        await this.loadPreviousQuestionSourceKeys(
          islandData.id,
          Number(islandData.order_index ?? 1),
        );
      if (
        !this.isIslandLoadCurrent(
          requestId,
          requestedSlug,
          requestedUserId,
          requestedLanguage,
        )
      )
        return;

      if (requestedUserId && this.stations.length > 0) {
        const { data: progressData, error: progressError } = await backend
          .from("station_progress")
          .select("*")
          .eq("user_id", requestedUserId)
          .in(
            "station_id",
            this.stations.map((station) => station.id),
          );

        if (progressError) throw progressError;
        if (
          !this.isIslandLoadCurrent(
            requestId,
            requestedSlug,
            requestedUserId,
            requestedLanguage,
          )
        )
          return;
        this.stationProgressRecords = (
          (progressData as unknown as StationProgress[]) || []
        ).filter((progress) => progress.user_id === requestedUserId);
        await this.repairCompletedIslandProgress(requestedUserId);
      }
    } catch (error) {
      if (
        !this.isIslandLoadCurrent(
          requestId,
          requestedSlug,
          requestedUserId,
          requestedLanguage,
        )
      )
        return;
      console.error("Error loading island:", error);
      this.applyLocalQuestionBank();
      this.toast.error(this.language.t("island.loadError"));
    } finally {
      if (
        this.isIslandLoadCurrent(
          requestId,
          requestedSlug,
          requestedUserId,
          requestedLanguage,
        )
      ) {
        this.loading = false;
        this.changeDetector.detectChanges();
      }
    }
  }

  applyLocalQuestionBank() {
    const preferredLanguage =
      this.language.language in
      (localQuestionBank.questions[0]?.languages ?? {})
        ? this.language.language
        : "en";
    const localStationIds =
      getQuestionBankStationIdsForIsland(this.slug) ??
      localQuestionBank.stations.map((station) => station.id);
    const localStationIdSet = new Set(localStationIds);
    const localStations = localQuestionBank.stations
      .filter((station) => localStationIdSet.has(station.id))
      .sort(
        (a, b) => localStationIds.indexOf(a.id) - localStationIds.indexOf(b.id),
      );
    const localQuestions = localQuestionBank.questions.filter((question) =>
      localStationIdSet.has(question.station_id),
    );

    this.usingLocalQuestionBank = true;
    this.stationProgressRecords = [];
    this.previousQuestionSourceKeys = new Set<string>();
    this.stations = localStations.map((station, index) => ({
      ...station,
      order_index: index + 1,
    }));
    this.allQuestions = localQuestions.map((question, index) => {
      const localized =
        question.languages[preferredLanguage] ?? question.languages.en;

      return {
        id: question.id,
        station_id: question.station_id,
        question_text: localized.question,
        explanation: localized.feedback,
        order_index: index + 1,
        stage: question.stage,
        point_value: question.complexity,
        complexity: question.complexity,
        status: question.status,
        human_review: question.human_review,
      };
    });
    this.answers = localQuestions.flatMap((question) => {
      const localized =
        question.languages[preferredLanguage] ?? question.languages.en;

      return question.answers.map((answer) => ({
        ...answer,
        answer_text:
          (localized.options as Record<string, string>)[answer.option_key] ??
          answer.answer_text,
      }));
    });
  }

  private async ensureSequentialIslandAccess(island: Island) {
    if (!this.user?.id) return false;
    if (Number(island.order_index ?? 1) <= 1) return true;

    try {
      let previousIslandQuery = backend
        .from("islands")
        .select("id, order_index, crew_id")
        .filter("order_index", "lt", Number(island.order_index))
        .order("order_index", { ascending: false })
        .limit(1);

      previousIslandQuery = island.crew_id
        ? previousIslandQuery.eq("crew_id", island.crew_id)
        : previousIslandQuery.eq("crew_id", null);

      const { data: previousIslands, error: previousIslandError } =
        await previousIslandQuery;
      if (previousIslandError) throw previousIslandError;

      const previousIsland = ((previousIslands as
        | Pick<Island, "id" | "order_index" | "crew_id">[]
        | null) || [])[0];
      if (!previousIsland) return true;

      const { data: progressData, error: progressError } = await backend
        .from("island_progress")
        .select("id, user_id, island_id, completed_at")
        .eq("user_id", this.user.id)
        .eq("island_id", previousIsland.id)
        .limit(1);

      if (progressError) throw progressError;

      const hasCompletedPrevious = (
        (progressData as
          | {
              user_id?: string | null;
              island_id?: string | null;
              completed_at?: string | null;
            }[]
          | null) || []
      ).some(
        (progress) =>
          progress.user_id === this.user?.id &&
          progress.island_id === previousIsland.id &&
          Boolean(progress.completed_at),
      );

      if (!hasCompletedPrevious) {
        this.toast.info(this.language.t("map.completePrevious"));
        await this.router.navigateByUrl("/");
        return false;
      }
    } catch (error) {
      console.warn("Could not verify island sequence access:", error);
      this.toast.info(this.language.t("map.completePrevious"));
      await this.router.navigateByUrl("/");
      return false;
    }

    return true;
  }

  private async repairCompletedIslandProgress(userId: string) {
    if (!this.user || this.user.id !== userId || !this.island) return;
    if (!this.areAllStationsComplete()) return;

    try {
      const { data, error } = await backend
        .from("island_progress")
        .select("user_id, island_id, completed_at")
        .eq("user_id", userId)
        .eq("island_id", this.island.id)
        .maybeSingle();

      if (error) throw error;

      const progress = data as
        | {
            user_id?: string | null;
            island_id?: string | null;
            completed_at?: string | null;
          }
        | null;
      if (
        progress?.user_id === userId &&
        progress?.island_id === this.island.id &&
        progress?.completed_at
      ) {
        return;
      }

      await this.persistIslandProgressScore(this.currentIslandPointScore());
    } catch (error) {
      console.warn("Could not repair completed island progress:", error);
    }
  }

  private async ensureCrewIslandAccess(islandId: string) {
    try {
      const { data, error } = await backend.rpc("get_island_access_status", {
        p_island_id: islandId,
      });

      if (error) throw error;

      const access = ((Array.isArray(data) ? data[0] : data) ||
        {}) as IslandAccessResult;
      if (access.can_access === false) {
        this.toast.info(this.language.t("map.crewAdvanceBlocked"));
        await this.router.navigateByUrl("/");
        return false;
      }
    } catch (error) {
      console.warn("Could not verify crew island access:", error);
    }

    return true;
  }

  startIsland() {
    if (this.stations[0]) void this.startStation(this.stations[0].id);
  }

  async startStation(stationId: string) {
    if (!this.island) return;

    if (this.isStationComplete(stationId)) {
      this.toast.info(this.language.t("island.stationAlreadyComplete"));
      return;
    }

    this.currentStationId = stationId;
    this.currentStage = 1;
    this.completedStages = [];
    this.totalScore = 0;
    this.totalCorrectCount = 0;
    this.stageScore = 0;
    this.stageCorrectCount = 0;
    this.currentQuestionIndex = 0;
    this.selectedAnswerId = null;
    this.submittingAnswer = false;
    this.currentExplanation = null;
    this.selectedAnswerText = null;
    this.correctAnswerText = null;
    this.lastEarnedPoints = 0;
    this.lastSpeedBonusPoints = 0;
    this.lastAnswerSeconds = 0;
    this.questionStartedAtMs = null;
    this.attemptId = this.usingLocalQuestionBank ? "local-question-bank" : null;

    const storedProgress = this.loadStationProgress(stationId);
    if (storedProgress) {
      let restoredSession = this.withStageCompletionState(
        storedProgress.session,
      );
      if (
        restoredSession.activeQuestionId &&
        restoredSession.answeredQuestionIds.includes(
          restoredSession.activeQuestionId,
        )
      ) {
        restoredSession = this.withStageCompletionState(
          advancePlaytestQuestion(restoredSession),
        );
      }
      if (restoredSession.isStageComplete && restoredSession.currentStage < 3) {
        restoredSession = this.withIslandDifficulty(
          advancePlaytestStage(restoredSession),
        );
      }

      this.playtestSession = this.withActiveQuestion(restoredSession);
      this.currentStage = this.playtestSession.currentStage;
      this.completedStages = storedProgress.completedStages;
      this.stageScore = this.playtestSession.stagePoints;
      this.totalScore = this.playtestSession.stationPoints;
      this.stageCorrectCount = this.playtestSession.history.filter(
        (item) =>
          item.stage === this.playtestSession?.currentStage && item.wasCorrect,
      ).length;
      this.totalCorrectCount = this.playtestSession.correctTotal;
      this.currentQuestionIndex = this.stageHistory(
        this.playtestSession,
        this.playtestSession.currentStage,
      ).length;

      if (
        !this.currentQuestion &&
        this.isCurrentStageFinished(this.playtestSession)
      ) {
        this.resetCurrentStageForRetry();
      }

      if (!this.currentQuestion) {
        this.toast.error(
          "No approved questions are available for this station stage yet.",
        );
        this.quizState = "intro";
        this.changeDetector.detectChanges();
        await this.saveStationProgress();
        return;
      }

      this.quizState = "quiz";
      this.changeDetector.detectChanges();
      await this.ensureAttempt();
      await this.saveStationProgress();
      return;
    }

    const assessment = await this.getLatestAssessmentCalibration();
    const session = createPlaytestSession({
      startingStage: 1,
      startingLevel:
        assessment.level + getIslandLevelBoost(this.island.order_index),
      initialLevelSource: assessment.source,
    });
    this.playtestSession = this.withActiveQuestion(session);

    if (!this.currentQuestion) {
      this.toast.error(
        "No approved questions are available for this station stage yet.",
      );
      this.quizState = "intro";
      return;
    }

    await this.ensureAttempt();

    this.quizState = "quiz";
    this.changeDetector.detectChanges();
    await this.saveStationProgress();
  }

  async startStage() {
    if (!this.currentStationId) return;
    await this.startStation(this.currentStationId);
  }

  private async getLatestAssessmentCalibration() {
    if (!this.user) return getStartingLevelFromAssessment(null);

    try {
      const { data, error } = await backend
        .from("self_assessment_attempts")
        .select("score")
        .eq("user_id", this.user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return getStartingLevelFromAssessment(
        Number((data as { score?: number } | null)?.score),
      );
    } catch {
      return getStartingLevelFromAssessment(null);
    }
  }

  private withActiveQuestion(session: PlaytestSession): PlaytestSession {
    const question = getPlaytestQuestion(
      this.allQuestions,
      session,
      this.currentStationId,
      Math.random,
      {
        avoidSourceKeys: this.previousQuestionSourceKeys,
      },
    );
    if (!question) return session;

    if (
      question.id !== session.activeQuestionId ||
      this.questionStartedAtMs === null
    ) {
      this.questionStartedAtMs = Date.now();
    }

    return { ...session, activeQuestionId: question.id };
  }

  private withIslandDifficulty(session: PlaytestSession): PlaytestSession {
    if (!this.island) return session;

    return {
      ...session,
      currentLevel: clampLevelForStage(
        session.currentLevel + getIslandLevelBoost(this.island.order_index),
        session.currentStage,
      ),
    };
  }

  private async loadPreviousQuestionSourceKeys(
    currentIslandId: string,
    currentOrderIndex: number,
  ) {
    if (!this.user?.id || this.usingLocalQuestionBank) return new Set<string>();

    try {
      const { data: previousIslands, error: previousIslandsError } =
        await backend
          .from("islands")
          .select("id")
          .filter("order_index", "lt", currentOrderIndex)
          .order("order_index", { ascending: true });

      if (previousIslandsError) throw previousIslandsError;
      const previousIslandIds = (
        (previousIslands as { id: string }[] | null) || []
      )
        .map((island) => island.id)
        .filter((islandId) => islandId !== currentIslandId);

      if (previousIslandIds.length === 0) return new Set<string>();

      const { data: previousStations, error: previousStationsError } =
        await backend
          .from("stations")
          .select("id")
          .in("island_id", previousIslandIds);

      if (previousStationsError) throw previousStationsError;
      const previousStationIds = (
        (previousStations as { id: string }[] | null) || []
      ).map((station) => station.id);
      if (previousStationIds.length === 0) return new Set<string>();

      const answeredQuestionIds = new Set<string>();
      for (const stationIdChunk of chunkArray(previousStationIds, 120)) {
        const { data: progressData, error: progressError } = await backend
          .from("station_progress")
          .select("state")
          .eq("user_id", this.user.id)
          .in("station_id", stationIdChunk);

        if (progressError) throw progressError;

        (
          (progressData as
            | { state?: PlaytestProgressSnapshot | null }[]
            | null) || []
        ).forEach((progress) => {
          progress.state?.session?.history?.forEach((historyItem) => {
            if (historyItem.questionId)
              answeredQuestionIds.add(historyItem.questionId);
          });
        });
      }

      if (answeredQuestionIds.size === 0) return new Set<string>();

      const sourceKeys = new Set<string>();
      for (const questionIdChunk of chunkArray([...answeredQuestionIds], 120)) {
        const { data: answeredQuestions, error: answeredQuestionsError } =
          await backend
            .from("questions_public")
            .select("id, source_question_id")
            .in("id", questionIdChunk);

        if (answeredQuestionsError) throw answeredQuestionsError;

        (
          (answeredQuestions as
            | Pick<Question, "id" | "source_question_id">[]
            | null) || []
        ).forEach((question) => {
          sourceKeys.add(getQuestionSourceKey(question));
        });
      }

      return sourceKeys;
    } catch (error) {
      console.warn("Could not load previously seen questions:", error);
      return new Set<string>();
    }
  }

  private get stationProgressKey() {
    if (!this.currentStationId) return null;

    return getPlaytestProgressKey({
      userId: this.user?.id,
      islandSlug: this.slug,
      stationId: this.currentStationId,
    });
  }

  private loadStationProgress(stationId: string) {
    const remoteProgress = this.stationProgressRecords.find(
      (progress) => progress.station_id === stationId,
    );
    const remoteSnapshot = remoteProgress
      ? this.snapshotFromStationProgress(remoteProgress)
      : null;

    if (remoteSnapshot) {
      return this.sanitizeProgressSnapshot(remoteSnapshot);
    }

    const storedSnapshot = loadStoredPlaytestProgress(
      window.localStorage,
      getPlaytestProgressKey({
        userId: this.user?.id,
        islandSlug: this.slug,
        stationId,
      }),
    );
    return storedSnapshot
      ? this.sanitizeProgressSnapshot(storedSnapshot)
      : null;
  }

  private sanitizeProgressSnapshot(
    snapshot: PlaytestProgressSnapshot,
  ): PlaytestProgressSnapshot {
    const session = this.withStageCompletionState(snapshot.session);
    return {
      ...snapshot,
      session,
      completedStages: [1, 2, 3].filter((stage) =>
        this.isStagePassed(session, stage),
      ),
    };
  }

  private async saveStationProgress() {
    if (!this.playtestSession || !this.stationProgressKey) return;

    const snapshot: PlaytestProgressSnapshot = {
      session: this.playtestSession,
      currentStage: this.playtestSession.currentStage,
      completedStages: this.completedStages,
      stageScore: this.stageScore,
      totalScore: this.totalScore,
      stageCorrectCount: this.stageCorrectCount,
      totalCorrectCount: this.totalCorrectCount,
      currentQuestionIndex: this.currentQuestionIndex,
    };

    saveStoredPlaytestProgress(
      window.localStorage,
      this.stationProgressKey,
      snapshot,
    );

    if (!this.user || !this.currentStationId)
      return;

    if (this.usingLocalQuestionBank) {
      await this.saveLocalQuestionBankStationProgress(snapshot);
      return;
    }

    const existingProgress = this.stationProgressRecords.find(
      (progress) => progress.station_id === this.currentStationId,
    );
    const isComplete = this.isCurrentStationComplete();
    const completedAt =
      existingProgress?.completed_at ??
      (isComplete ? new Date().toISOString() : null);

    try {
      const { data, error } = await backend
        .from("station_progress")
        .upsert(
          {
            user_id: this.user.id,
            station_id: this.currentStationId,
            current_stage: this.playtestSession.currentStage,
            stage_points: this.playtestSession.stagePoints,
            station_points: this.playtestSession.stationPoints,
            completed_at: completedAt,
            state: snapshot,
          },
          { onConflict: "user_id,station_id" },
        )
        .select("*")
        .single();

      if (error) throw error;
      const savedProgress = data as unknown as StationProgress;
      if (savedProgress.user_id !== this.user.id) {
        throw new Error("Saved station progress belongs to a different user");
      }
      this.stationProgressRecords = [
        ...this.stationProgressRecords.filter(
          (progress) => progress.station_id !== savedProgress.station_id,
        ),
        savedProgress,
      ];
    } catch (error) {
      console.error("Error saving station progress:", error);
    }
  }

  private createRemoteStationIdMap(stations: Station[]) {
    const stationIdMap = new Map<string, string>();

    stations.forEach((station) => {
      [station.source_station_id, station.slug, station.id].forEach((key) => {
        const normalizedKey = normalizedStationRouteKey(key);
        if (normalizedKey && !stationIdMap.has(normalizedKey)) {
          stationIdMap.set(normalizedKey, station.id);
        }
      });
    });

    return stationIdMap;
  }

  private async saveLocalQuestionBankStationProgress(
    snapshot: PlaytestProgressSnapshot,
  ) {
    if (!this.user || !this.currentStationId) return;

    const remoteStationId = this.remoteStationIdsByLocalStationId.get(
      normalizedStationRouteKey(this.currentStationId),
    );
    if (!remoteStationId) return;

    const isComplete = this.isCurrentStationComplete();
    const existingProgress = this.stationProgressRecords.find(
      (progress) => progress.station_id === remoteStationId,
    );
    const completedAt =
      existingProgress?.completed_at ??
      (isComplete ? new Date().toISOString() : null);
    const payload = {
      current_stage: this.playtestSession?.currentStage ?? snapshot.currentStage,
      stage_points: this.playtestSession?.stagePoints ?? snapshot.stageScore,
      station_points:
        this.playtestSession?.stationPoints ?? snapshot.totalScore,
      completed_at: completedAt,
      state: snapshot,
    };

    try {
      let savedProgress: StationProgress | null = null;

      if (existingProgress) {
        const { data, error } = await backend
          .from("station_progress")
          .update(payload)
          .eq("user_id", this.user.id)
          .eq("station_id", remoteStationId)
          .select("*")
          .maybeSingle();
        if (error) throw error;
        savedProgress = data as StationProgress | null;
      } else {
        const { data, error } = await backend
          .from("station_progress")
          .insert({
            user_id: this.user.id,
            station_id: remoteStationId,
            ...payload,
          })
          .select("*")
          .maybeSingle();

        if (error) {
          const updateResult = await backend
            .from("station_progress")
            .update(payload)
            .eq("user_id", this.user.id)
            .eq("station_id", remoteStationId)
            .select("*")
            .maybeSingle();
          if (updateResult.error) throw updateResult.error;
          savedProgress = updateResult.data as StationProgress | null;
        } else {
          savedProgress = data as StationProgress | null;
        }
      }

      if (savedProgress) {
        this.stationProgressRecords = [
          ...this.stationProgressRecords.filter(
            (progress) => progress.station_id !== savedProgress?.station_id,
          ),
          savedProgress,
        ];
      }
    } catch (error) {
      console.error("Error saving local question bank station progress:", error);
    }
  }

  private clearStationProgress() {
    if (!this.stationProgressKey) return;
    clearStoredPlaytestProgress(window.localStorage, this.stationProgressKey);
  }

  private snapshotFromStationProgress(
    progress: StationProgress,
  ): PlaytestProgressSnapshot | null {
    if (progress.state?.session) {
      const savedSession = progress.state.session;
      const currentStage = clampStage(
        Math.max(
          Number(progress.current_stage ?? 1),
          Number(progress.state.currentStage ?? 1),
          Number(savedSession.currentStage ?? 1),
        ),
      );
      const stageMatchesSession = savedSession.currentStage === currentStage;
      const session: PlaytestSession = {
        ...savedSession,
        currentStage,
        stagePoints: stageMatchesSession
          ? savedSession.stagePoints
          : (progress.stage_points ?? 0),
        answeredQuestionIds: stageMatchesSession
          ? savedSession.answeredQuestionIds
          : [],
        activeQuestionId: stageMatchesSession
          ? savedSession.activeQuestionId
          : null,
        lastQuestionId: stageMatchesSession
          ? savedSession.lastQuestionId
          : null,
        isStageComplete: stageMatchesSession
          ? savedSession.isStageComplete
          : false,
        isComplete: stageMatchesSession ? savedSession.isComplete : false,
      };
      const currentStageHistoryCount = session.history.filter(
        (item) => item.stage === currentStage,
      ).length;

      return {
        session,
        currentStage,
        completedStages: progress.state.completedStages ?? [],
        stageScore:
          progress.state.stageScore ??
          session.stagePoints ??
          progress.stage_points ??
          0,
        totalScore:
          progress.state.totalScore ??
          session.stationPoints ??
          progress.station_points ??
          0,
        stageCorrectCount: progress.state.stageCorrectCount ?? 0,
        totalCorrectCount:
          progress.state.totalCorrectCount ?? session.correctTotal ?? 0,
        currentQuestionIndex: stageMatchesSession
          ? (progress.state.currentQuestionIndex ?? currentStageHistoryCount)
          : currentStageHistoryCount,
      };
    }

    return null;
  }

  private async ensureAttempt() {
    if (
      this.usingLocalQuestionBank ||
      !this.user ||
      !this.island ||
      this.attemptId
    )
      return;

    try {
      const { data, error } = await backend
        .from("quiz_attempts")
        .insert({
          user_id: this.user.id,
          island_id: this.island.id,
          score: 0,
          total_questions: this.stageQuestions.length,
        })
        .select()
        .single();

      if (error) throw error;
      this.attemptId = data.id;
    } catch (error) {
      console.error("Error creating quiz attempt:", error);
    }
  }

  private isCurrentStationComplete() {
    return [1, 2, 3].every((stage) => this.completedStages.includes(stage));
  }

  private currentAnswerSeconds() {
    if (this.questionStartedAtMs === null)
      return SPEED_BONUS_WINDOWS[SPEED_BONUS_WINDOWS.length - 1].seconds + 1;
    return Math.max(
      0,
      Math.round((Date.now() - this.questionStartedAtMs) / 1000),
    );
  }

  private answerSpeedBonus(answerSeconds: number) {
    return getSpeedBonusPoints(answerSeconds);
  }

  private stageQuestionCount(stage: number) {
    return this.questionsForStage(stage).length;
  }

  private stageHistory(session: PlaytestSession, stage: number) {
    return session.history.filter((item) => item.stage === stage);
  }

  private isStageFinished(session: PlaytestSession, stage: number) {
    const questionCount = this.stageQuestionCount(stage);
    if (questionCount === 0) return false;
    return this.stageHistory(session, stage).length >= questionCount;
  }

  private isStagePassed(session: PlaytestSession, stage: number) {
    const questionCount = this.stageQuestionCount(stage);
    if (questionCount === 0 || !this.isStageFinished(session, stage))
      return false;
    const correctCount = this.stageHistory(session, stage).filter(
      (item) => item.wasCorrect,
    ).length;
    return (correctCount / questionCount) * 100 >= 70;
  }

  isCurrentStageFinished(session = this.playtestSession) {
    if (!session) return false;
    return this.isStageFinished(session, session.currentStage);
  }

  private isCurrentStagePassed(session = this.playtestSession) {
    if (!session) return false;
    return this.isStagePassed(session, session.currentStage);
  }

  private withStageCompletionState(session: PlaytestSession): PlaytestSession {
    const isStageComplete = this.isCurrentStagePassed(session);
    return {
      ...session,
      isStageComplete,
      isComplete: session.currentStage >= 3 && isStageComplete,
    };
  }

  selectAnswer(answerId: string) {
    if (this.quizState !== "quiz" || this.submittingAnswer) return;

    this.selectedAnswerId = answerId;
    this.changeDetector.detectChanges();
  }

  async submitAnswer(answerId = this.selectedAnswerId) {
    const question = this.currentQuestion;
    if (
      !answerId ||
      !question ||
      !this.playtestSession ||
      this.quizState !== "quiz" ||
      this.submittingAnswer
    )
      return;

    this.selectedAnswerId = answerId;
    this.submittingAnswer = true;
    this.changeDetector.detectChanges();

    const playFeedbackSound = createQuizSoundPlayer();
    const selectedAnswer = this.answers.find(
      (answer) => answer.id === answerId,
    );
    if (!selectedAnswer || selectedAnswer.question_id !== question.id) {
      this.submittingAnswer = false;
      this.changeDetector.detectChanges();
      this.toast.error(this.language.t("island.submitError"));
      return;
    }
    const answerSeconds = this.currentAnswerSeconds();
    const speedBonusPoints = this.answerSpeedBonus(answerSeconds);

    try {
      if (this.usingLocalQuestionBank) {
        const correctAnswer = this.answers.find(
          (answer) => answer.question_id === question.id && answer.is_correct,
        );
        const correct = Boolean(selectedAnswer.is_correct);
        const nextSession = applyPlaytestAnswer(
          this.playtestSession,
          question,
          correct,
          {
            speedBonusPoints,
            answerSeconds,
            requiredQuestionCount: this.stageQuestions.length,
            passingPercent: 70,
          },
        );
        const historyItem = nextSession.history[nextSession.history.length - 1];

        this.playtestSession = nextSession;
        this.currentStage = nextSession.currentStage;
        this.stageScore = nextSession.stagePoints;
        this.stageCorrectCount = nextSession.history.filter(
          (item) => item.stage === nextSession.currentStage && item.wasCorrect,
        ).length;
        this.isCorrect = correct;
        this.currentExplanation = question.explanation || null;
        this.selectedAnswerText = selectedAnswer.answer_text || null;
        this.correctAnswerText = correctAnswer?.answer_text || null;
        this.lastEarnedPoints = historyItem?.earnedPoints ?? 0;
        this.lastSpeedBonusPoints = historyItem?.speedBonusPoints ?? 0;
        this.lastAnswerSeconds = historyItem?.answerSeconds ?? answerSeconds;
        this.quizState = "feedback";
        this.changeDetector.detectChanges();
        await this.saveStationProgress();
        playFeedbackSound(correct);
        return;
      }

      await this.ensureAttempt();

      const { data: checkResult, error: checkError } = await backend.rpc(
        "check_answer",
        {
          p_question_id: question.id,
          p_answer_id: answerId,
          p_language: this.language.language,
        },
      );

      if (checkError) throw checkError;

      const result = (checkResult?.[0] || {}) as CheckAnswerResult;
      const correct = result.is_correct || false;
      const earnedPoints = Number(
        result.earned_points ??
          (correct
            ? (result.point_value ?? getQuestionPointValue(question))
            : 0),
      );
      const nextSession = applyPlaytestAnswer(
        this.playtestSession,
        question,
        correct,
        {
          speedBonusPoints,
          answerSeconds,
          requiredQuestionCount: this.stageQuestions.length,
          passingPercent: 70,
        },
      );
      const historyItem = nextSession.history[nextSession.history.length - 1];

      this.playtestSession = nextSession;
      this.isCorrect = correct;
      this.currentExplanation = result.explanation || null;
      this.selectedAnswerText =
        result.selected_answer_text || selectedAnswer.answer_text || null;
      this.correctAnswerText =
        result.correct_answer_text ||
        (correct
          ? result.selected_answer_text || selectedAnswer.answer_text || null
          : null);
      this.lastEarnedPoints =
        historyItem?.earnedPoints ??
        (Number.isFinite(earnedPoints) ? Math.max(0, earnedPoints) : 0);
      this.lastSpeedBonusPoints = historyItem?.speedBonusPoints ?? 0;
      this.lastAnswerSeconds = historyItem?.answerSeconds ?? answerSeconds;
      this.quizState = "feedback";
      this.changeDetector.detectChanges();
      playFeedbackSound(correct);
      this.stageScore = nextSession.stagePoints;
      this.stageCorrectCount = nextSession.history.filter(
        (item) => item.stage === nextSession.currentStage && item.wasCorrect,
      ).length;
      await this.saveStationProgress();

      if (this.attemptId) {
        await backend.from("question_responses").insert({
          attempt_id: this.attemptId,
          question_id: question.id,
          selected_answer_id: answerId,
          is_correct: correct,
        });
      }
    } catch (error) {
      console.error("Error checking answer:", error);
      this.toast.error(this.language.t("island.submitError"));
    } finally {
      this.submittingAnswer = false;
      this.changeDetector.detectChanges();
    }
  }

  async nextQuestion() {
    if (!this.playtestSession) return;

    const shouldCompleteStage = this.isCurrentStageFinished(
      this.playtestSession,
    );

    this.selectedAnswerId = null;
    this.submittingAnswer = false;
    this.currentExplanation = null;
    this.selectedAnswerText = null;
    this.correctAnswerText = null;
    this.isCorrect = false;
    this.lastEarnedPoints = 0;
    this.lastSpeedBonusPoints = 0;
    this.lastAnswerSeconds = 0;

    if (shouldCompleteStage) {
      this.changeDetector.detectChanges();
      await this.completeStage();
    } else {
      this.playtestSession = this.withActiveQuestion(
        this.withStageCompletionState(
          advancePlaytestQuestion(this.playtestSession),
        ),
      );
      this.currentQuestionIndex = this.stageHistory(
        this.playtestSession,
        this.playtestSession.currentStage,
      ).length;
      this.quizState = "quiz";
      this.changeDetector.detectChanges();
      await this.saveStationProgress();
    }
  }

  async completeStage() {
    if (!this.playtestSession) return;

    this.playtestSession = this.withStageCompletionState(this.playtestSession);
    const stagePassed = this.isCurrentStagePassed(this.playtestSession);

    if (stagePassed) {
      this.completedStages = Array.from(
        new Set([...this.completedStages, this.currentStage]),
      );
      this.totalScore = this.playtestSession.stationPoints;
      this.totalCorrectCount = this.playtestSession.correctTotal;
    }

    if (this.usingLocalQuestionBank || !this.island || !this.user) {
      this.quizState = "stage-results";
      this.attemptId = null;
      await this.saveStationProgress();
      await this.saveCompletedIslandProgressIfReady();
      return;
    }

    try {
      if (this.attemptId) {
        await backend
          .from("quiz_attempts")
          .update({
            completed_at: new Date().toISOString(),
            score: this.stageScore,
          })
          .eq("id", this.attemptId);
      }

      this.quizState = "stage-results";
      this.attemptId = null;
      await this.saveStationProgress();
      await this.saveCompletedIslandProgressIfReady();
    } catch (error) {
      console.error("Error completing stage:", error);
      this.quizState = "stage-results";
      this.attemptId = null;
      await this.saveStationProgress();
      await this.saveCompletedIslandProgressIfReady();
    }
  }

  async proceedAfterStage() {
    const stagePassed = this.isCurrentStagePassed(this.playtestSession);

    if (!stagePassed) {
      this.resetCurrentStageForRetry();
      this.quizState = "quiz";
      this.changeDetector.detectChanges();
      await this.saveStationProgress();
      return;
    }

    if (this.playtestSession && this.currentStage < 3) {
      this.playtestSession = this.withActiveQuestion(
        this.withIslandDifficulty(advancePlaytestStage(this.playtestSession)),
      );
      this.currentStage = this.playtestSession.currentStage;
      this.currentQuestionIndex = this.stageHistory(
        this.playtestSession,
        this.playtestSession.currentStage,
      ).length;
      this.stageScore = 0;
      this.stageCorrectCount = 0;
      this.selectedAnswerId = null;
      this.submittingAnswer = false;
      this.currentExplanation = null;
      this.selectedAnswerText = null;
      this.correctAnswerText = null;
      this.lastEarnedPoints = 0;
      this.lastSpeedBonusPoints = 0;
      this.lastAnswerSeconds = 0;

      if (!this.currentQuestion) {
        this.toast.error(
          "No approved questions are available for this station stage yet.",
        );
        this.quizState = "intro";
        this.changeDetector.detectChanges();
        await this.saveStationProgress();
        return;
      }

      this.quizState = "quiz";
      this.changeDetector.detectChanges();
      await this.ensureAttempt();
      await this.saveStationProgress();
      return;
    }

    await this.saveStationProgress();

    if (this.areAllStationsComplete()) {
      await this.completeIsland();
      return;
    }

    this.quizState = "intro";
    this.changeDetector.detectChanges();
  }

  private resetCurrentStageForRetry() {
    if (!this.playtestSession) return;

    const previousHistory = this.playtestSession.history.filter(
      (item) => item.stage !== this.currentStage,
    );
    const stationPoints = previousHistory.reduce(
      (sum, item) => sum + item.earnedPoints,
      0,
    );
    const correctTotal = previousHistory.filter(
      (item) => item.wasCorrect,
    ).length;
    const incorrectTotal = previousHistory.length - correctTotal;
    const retrySession = createPlaytestSession({
      startingStage: this.currentStage,
      startingLevel: this.playtestSession.currentLevel,
      initialLevelSource: this.playtestSession.initialLevelSource,
    });

    this.playtestSession = this.withActiveQuestion({
      ...retrySession,
      stationPoints,
      correctTotal,
      incorrectTotal,
      history: previousHistory,
    });
    this.stageScore = 0;
    this.stageCorrectCount = 0;
    this.totalScore = stationPoints;
    this.totalCorrectCount = correctTotal;
    this.currentQuestionIndex = 0;
    this.selectedAnswerId = null;
    this.submittingAnswer = false;
    this.currentExplanation = null;
    this.selectedAnswerText = null;
    this.correctAnswerText = null;
    this.isCorrect = false;
    this.lastEarnedPoints = 0;
    this.lastSpeedBonusPoints = 0;
    this.lastAnswerSeconds = 0;
  }

  async completeIsland() {
    if (!this.island) return;

    const finalPointScore = this.currentIslandPointScore();

    if (!this.user) {
      this.quizState = "results";
      return;
    }

    if (this.usingLocalQuestionBank) {
      await this.finishIslandWithPersistedProgress(finalPointScore);
      return;
    }

    try {
      const { data, error } = await backend.rpc(
        "complete_island_if_stations_done",
        {
          p_island_id: this.island.id,
        },
      );

      if (error) throw error;

      const result = ((Array.isArray(data) ? data[0] : data) ||
        {}) as CompleteIslandResult;
      if (!result.completed) {
        console.warn("Island completion RPC did not mark the island done:", {
          islandId: this.island.id,
          completedStationCount: result.completed_station_count,
          stationCount: result.station_count,
        });
        await this.finishIslandWithPersistedProgress(finalPointScore);
        return;
      }

      await this.finishIslandWithPersistedProgress(finalPointScore);
    } catch (error) {
      console.error("Error saving island progress:", error);
      await this.finishIslandWithPersistedProgress(finalPointScore);
    }
  }

  private normalizeScore(score: number) {
    const numericScore = Number(score);
    return Number.isFinite(numericScore)
      ? Math.max(0, Math.trunc(numericScore))
      : 0;
  }

  private currentIslandPointScore() {
    return this.normalizeScore(
      this.stations.reduce(
        (sum, station) => sum + this.stationPoints(station.id),
        0,
      ),
    );
  }

  private async finishIslandWithPersistedProgress(score: number) {
    const finalScore = this.normalizeScore(score);
    const persisted = await this.persistIslandProgressScore(finalScore);

    if (!persisted) {
      this.toast.error(this.language.t("island.loadError"));
      this.quizState = "intro";
      return;
    }

    this.totalScore = finalScore;
    this.quizState = "results";
    await syncCurrentUserGameProgress();
  }

  private async saveCompletedIslandProgressIfReady() {
    if (!this.user || !this.island || !this.areAllStationsComplete()) return;

    const persisted = await this.persistIslandProgressScore(
      this.currentIslandPointScore(),
    );
    if (persisted) {
      await syncCurrentUserGameProgress();
    }
  }

  private async persistIslandProgressScore(score: number) {
    if (!this.user || !this.island) return false;

    try {
      const { data: existingRows, error: existingError } = await backend
        .from("island_progress")
        .select("user_id, island_id, score, completed_at")
        .eq("user_id", this.user.id)
        .eq("island_id", this.island.id)
        .limit(1);
      if (existingError) throw existingError;

      const existingProgress = (((existingRows as unknown[]) || [])[0] ??
        null) as
        | {
            user_id?: string | null;
            island_id?: string | null;
            score?: number | null;
            completed_at?: string | null;
          }
        | null;
      const finalScore = Math.max(
        this.normalizeScore(score),
        this.normalizeScore(existingProgress?.score ?? 0),
      );
      const completedAt =
        existingProgress?.completed_at ?? new Date().toISOString();

      if (existingProgress?.island_id === this.island.id) {
        const { error: updateError } = await backend
          .from("island_progress")
          .update({
            score: finalScore,
            completed_at: completedAt,
          })
          .eq("user_id", this.user.id)
          .eq("island_id", this.island.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await backend
          .from("island_progress")
          .insert({
            user_id: this.user.id,
            island_id: this.island.id,
            score: finalScore,
            completed_at: completedAt,
          });

        if (insertError) {
          const { error: updateError } = await backend
            .from("island_progress")
            .update({
              score: finalScore,
              completed_at: completedAt,
            })
            .eq("user_id", this.user.id)
            .eq("island_id", this.island.id);
          if (updateError) throw updateError;
        }
      }

      return true;
    } catch (error) {
      console.warn("Could not normalize island progress score:", error);
      return false;
    }
  }

  private resetRuntimeProgressState() {
    this.quizState = "intro";
    this.currentStage = 1;
    this.currentQuestionIndex = 0;
    this.stageScore = 0;
    this.totalScore = 0;
    this.stageCorrectCount = 0;
    this.totalCorrectCount = 0;
    this.selectedAnswerId = null;
    this.submittingAnswer = false;
    this.currentExplanation = null;
    this.selectedAnswerText = null;
    this.correctAnswerText = null;
    this.lastEarnedPoints = 0;
    this.lastSpeedBonusPoints = 0;
    this.lastAnswerSeconds = 0;
    this.attemptId = null;
    this.completedStages = [];
    this.currentStationId = null;
    this.playtestSession = null;
    this.questionStartedAtMs = null;
    this.stationProgressRecords = [];
    this.remoteStationIdsByLocalStationId = new Map<string, string>();
  }

  resetQuiz() {
    this.clearStationProgress();
    this.resetRuntimeProgressState();
  }

  questionsForStage(stage: number) {
    return getPlayableQuestions(
      this.allQuestions,
      this.currentStationId,
      stage,
    ) as Question[];
  }

  stationPoints(stationId: string) {
    if (this.currentStationId === stationId && this.playtestSession) {
      return this.playtestSession.stationPoints;
    }

    const remoteProgress = this.stationProgressRecords.find(
      (progress) => progress.station_id === stationId,
    );
    return (
      remoteProgress?.station_points ??
      this.loadStationProgress(stationId)?.session.stationPoints ??
      0
    );
  }

  isStationComplete(stationId: string) {
    if (this.currentStationId === stationId && this.isCurrentStationComplete())
      return true;

    const remoteProgress = this.stationProgressRecords.find(
      (progress) => progress.station_id === stationId,
    );
    if (remoteProgress?.completed_at) return true;

    const storedProgress = this.loadStationProgress(stationId);
    return storedProgress
      ? [1, 2, 3].every((stage) =>
          storedProgress.completedStages.includes(stage),
        )
      : false;
  }

  stationStatusText(stationId: string) {
    if (this.isStationComplete(stationId))
      return this.language.t("island.stationCompleteShort");

    const progress = this.stationProgressRecords.find(
      (item) => item.station_id === stationId,
    );
    const snapshot = this.loadStationProgress(stationId);
    const stage =
      progress?.current_stage ?? snapshot?.session.currentStage ?? 1;
    return this.language.t("island.nextStationStage", { stage });
  }

  stationCardClass(stationId: string) {
    return cn(
      "text-left rounded-2xl border p-6 transition-all",
      this.isStationComplete(stationId)
        ? "cursor-not-allowed border-accent/30 bg-accent/10 opacity-80"
        : "border-primary/20 bg-background/70 hover:-translate-y-1 hover:border-primary hover:shadow-xl",
    );
  }

  areAllStationsComplete() {
    return (
      this.stations.length > 0 &&
      this.stations.every((station) => this.isStationComplete(station.id))
    );
  }

  questionSetPoints(questions: Question[]) {
    return getQuestionSetPoints(questions);
  }

  questionPointValue(question?: Question | null) {
    return getQuestionPointValue(question);
  }

  answerLetter(index: number) {
    return String.fromCharCode(65 + index);
  }

  stageIconClass(stage: number) {
    return cn(
      "w-10 h-10 rounded-full flex items-center justify-center text-xl bg-gradient-to-br",
      STAGE_COLORS[stage - 1],
    );
  }

  currentStageIconClass() {
    return cn(
      "w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center shadow-lg text-4xl bg-gradient-to-br",
      STAGE_COLORS[this.currentStage - 1],
    );
  }

  stagePillClass(stage: number) {
    return cn(
      "px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r",
      STAGE_COLORS[stage - 1],
    );
  }

  stageButtonClass(stage: number, extra = "") {
    return buttonClasses(
      "default",
      "lg",
      cn(
        "text-white shadow-lg bg-gradient-to-r hover:opacity-90",
        STAGE_COLORS[stage - 1],
        extra,
      ),
    );
  }

  answerChoiceClass(answerId: string) {
    if (this.quizState === "feedback") {
      const answer = this.answers.find(
        (candidate) => candidate.id === answerId,
      );
      const isSelectedAnswer = this.selectedAnswerId === answerId;
      const isKnownCorrectAnswer =
        answer?.is_correct ||
        Boolean(
          this.correctAnswerText &&
          answer?.answer_text &&
          answer.answer_text === this.correctAnswerText,
        );

      return cn(
        "w-full p-4 text-left rounded-lg border-2 transition-all disabled:opacity-100",
        isSelectedAnswer && this.isCorrect
          ? "border-accent bg-accent/10 text-foreground"
          : isSelectedAnswer
            ? "border-destructive bg-destructive/10 text-foreground"
            : isKnownCorrectAnswer
              ? "border-accent bg-accent/10 text-foreground"
              : "border-border bg-background/60 text-foreground",
      );
    }

    return cn(
      "w-full p-4 text-left rounded-lg border-2 transition-all",
      this.selectedAnswerId === answerId
        ? "border-primary bg-primary/10 text-foreground"
        : "border-border hover:border-muted-foreground hover:bg-muted/50",
    );
  }

  stageResultIconClass() {
    return cn(
      "w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center shadow-lg text-4xl bg-gradient-to-br",
      this.stagePercent >= 70
        ? STAGE_COLORS[this.currentStage - 1]
        : "from-muted to-muted-foreground/50",
    );
  }

  stageProgressClass(stage: number) {
    return this.completedStages.includes(stage) ||
      (stage === this.currentStage && this.stagePercent >= 70)
      ? cn("bg-gradient-to-br text-white", STAGE_COLORS[stage - 1])
      : "bg-muted text-muted-foreground";
  }

  stageResultButtonClass() {
    const color =
      this.stagePercent >= 70 && this.currentStage < 3
        ? STAGE_COLORS[this.currentStage]
        : this.stagePercent >= 70
          ? "from-primary to-accent"
          : STAGE_COLORS[this.currentStage - 1];

    return buttonClasses(
      "default",
      "default",
      cn("flex-1 text-white bg-gradient-to-r hover:opacity-90", color),
    );
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  progressBarStyle = progressBarStyle;
}
